import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { ParamContext } from '../../service/param.context';
import { SERVER_API_URL } from '../../../app.constants';
import { Observable } from 'rxjs';
import { createRequestOption } from '../..';
import { StudySearchRequest } from '../model/study-search-request.model';
import { StudySearchResponse } from '../model/study-search-response.model';
import { StudyDetails } from '../model/study-details.model';
import { StudyType } from '../../model/study-type.model';
import { CreateStudyRequest } from '../model/create-study-request.model';
import { UpdateStudyBasicDetailsRequest } from '../model/update-study-basic-details-request.model';
import { EnableLoadingIndicator, EnablePageBlocking, EnableSavingIndicator } from '../../loading-indicator';

@Injectable()
export class StudyService {

    constructor(
        private http: HttpClient,
        private paramContext: ParamContext
    ) {
    }

    downloadImportTemplate(fileNamePrefix: string, isUpdateFormat = true) {
        let url = SERVER_API_URL + `crops/${this.paramContext.cropName}/germplasm-lists/templates/xlsx/${isUpdateFormat}?programUUID=${this.paramContext.programUUID}`;
        if (fileNamePrefix) {
            url += `&fileNamePrefix=${fileNamePrefix}`;
        }

        return this.http.get(url, { observe: 'response', responseType: 'blob' });
    }

    importStudyEntries(studyId: number, data: any[], newVariables: any[]): Observable<any> {
        const params: any = { data, newVariables };
        const url = SERVER_API_URL
            + `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/entries/import`;
        return this.http.post(url, params, { context: new HttpContext().set(EnableSavingIndicator, true).set(EnablePageBlocking, true) });
    }

    searchStudies(req: StudySearchRequest, pagination: any): Observable<HttpResponse<StudySearchResponse[]>> {
        const params = createRequestOption(pagination);
        const url = SERVER_API_URL + `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/search`;
        return this.http.post<StudySearchResponse[]>(url, req, { params, observe: 'response' });
    }

    getStudyDetails(studyId: number): Observable<HttpResponse<StudyDetails>> {
        const url = SERVER_API_URL + `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/details`;
        return this.http.get<StudyDetails>(url, { observe: 'response' , context: new HttpContext().set(EnableLoadingIndicator, true) });
    }

    getStudyTypes(cropName: string, programId: string): Observable<StudyType[]> {
        return this.http.get<StudyType[]>(`/bmsapi/crops/${cropName}/study-types/visible`, {
            params: { programUUID: programId }
        });
    }

    createStudy(req: CreateStudyRequest): Observable<HttpResponse<StudyDetails>> {
        const url = SERVER_API_URL + `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies`;
        return this.http.put<StudyDetails>(url, req, { observe: 'response' });
    }

    updateStudyBasicDetails(req: UpdateStudyBasicDetailsRequest): Observable<HttpResponse<StudyDetails>> {
        const url = SERVER_API_URL + `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${req.studyId}/basic-details`;
        return this.http.patch<StudyDetails>(url, req, { observe: 'response', context: new HttpContext().set(EnableSavingIndicator, true).set(EnablePageBlocking, true) });
    }

    changeLockedStatus(studyId: number, studyStatusRequest) {
        const url = SERVER_API_URL + `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}`;
        return this.http.patch(url, studyStatusRequest, { observe: 'response' });
    }

    deleteStudy(studyId: number) {
        const url = SERVER_API_URL + `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}`;
        return this.http.delete(url, { observe: 'response' });
    }

    studyHasSamples(studyId: number) {
        const url = SERVER_API_URL + `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/sampled`;
        return this.http.get(url, { observe: 'response' });
    };

}
